import { createId } from './document';
import { findLayer, isGroup, locateLayer } from './tree';
import type { EditorDocument, GroupLayer, Layer } from './types';

/**
 * Grouping and ungrouping — the model half.
 *
 * A group is an organisational container (see `docs/DESIGN-iteration-11.md`
 * §2.1): it owns a name, a visibility flag and a lock, and its children keep
 * their own geometry. That is what makes grouping a **pixel-neutral**
 * operation — the canvas shows the same thing before and after — and what makes
 * ungrouping exactly reversible.
 *
 * Which layers get grouped? The selection (Iteration 12). One id keeps the
 * original rule — the active layer and the one directly below it in the same
 * list — which is the muscle memory Ctrl+G built in Iteration 11. Two or more
 * ids group exactly what was selected, and they have to be siblings: grouping
 * layers from two different lists would reorder the document or invent
 * nesting, and neither is the command's job.
 */

export type GroupRejection =
  | 'not-found'
  | 'not-a-group'
  | 'locked'
  | 'no-children'
  | 'mixed-parents';

export interface GroupPlan {
  /** The group to insert. */
  group: GroupLayer;
  /** Where it goes in its parent's list. */
  index: number;
  /** The layers it takes in, bottom-first. */
  children: Layer[];
}

export interface GroupResult {
  plan: GroupPlan | null;
  reason?: GroupRejection;
}

export const GROUP_REJECTION_MESSAGES: Record<GroupRejection, string> = {
  'not-found': 'That layer no longer exists.',
  'not-a-group': 'That layer is not a group.',
  locked: 'That group is locked.',
  'no-children': 'That group is empty.',
  'mixed-parents': 'Select layers in one group to group them.',
};

/** How many groups the document already has, for the default name. */
export function groupCount(layers: Layer[]): number {
  let count = 0;
  const walk = (siblings: Layer[]) => {
    for (const layer of siblings) {
      if (!isGroup(layer)) continue;
      count += 1;
      walk(layer.children);
    }
  };
  walk(layers);
  return count;
}

/**
 * The plan for grouping a selection.
 *
 * With one id (or none), the rule is the one Iteration 11 shipped: the active
 * layer and the one directly below it, and a bottom layer groups on its own —
 * an empty folder is a legitimate thing to want. With two or more ids, the
 * selected layers are the children, in the order they sit in their list, and
 * they must all share one parent.
 *
 * The group is inserted where the lower of the grouped layers sat, and the
 * children keep their positions, scales and angles untouched — grouping is a
 * container operation, not a geometric one.
 */
export function planGroup(doc: EditorDocument, ids: string | string[]): GroupResult {
  const requested = Array.isArray(ids) ? ids : [ids];
  const firstId = requested[0];
  if (!firstId) return { plan: null, reason: 'not-found' };

  const location = locateLayer(doc.layers, firstId);
  if (!location) return { plan: null, reason: 'not-found' };
  const active = location.siblings[location.index];
  if (!active) return { plan: null, reason: 'not-found' };

  // One id: the pair rule. Two or more: exactly the selection, which must be
  // siblings — every requested id has to live in the same list as the first.
  let children: Layer[];
  let at: number;
  if (requested.length === 1) {
    if (active.locked) return { plan: null, reason: 'locked' };
    const below = location.index > 0 ? location.siblings[location.index - 1] : undefined;
    children = below ? [below, active] : [active];
    at = below ? location.index - 1 : location.index;
  } else {
    const unique = new Set(requested);
    const siblingIds = new Set(location.siblings.map((candidate) => candidate.id));
    for (const id of unique) {
      if (siblingIds.has(id)) continue;
      // Elsewhere in the tree is a mixed-parent selection; nowhere at all is
      // a stale id, which the caller wants to hear about differently.
      return { plan: null, reason: findLayer(doc.layers, id) ? 'mixed-parents' : 'not-found' };
    }
    for (const candidate of location.siblings) {
      if (unique.has(candidate.id) && candidate.locked) return { plan: null, reason: 'locked' };
    }
    // Bottom-first, exactly as they sit: the group's child order is the list's
    // order, so nothing renders differently inside the folder.
    children = location.siblings.filter((candidate) => unique.has(candidate.id));
    at = location.siblings.findIndex((candidate) => unique.has(candidate.id));
  }

  const group: GroupLayer = {
    id: createId(),
    name: `Group ${groupCount(doc.layers) + 1}`,
    type: 'group',
    visible: true,
    locked: false,
    // Neutral geometry: the group has no position of its own, so nothing about
    // the canvas can change when the children move into it.
    opacity: 1,
    blendMode: 'normal',
    x: 0,
    y: 0,
    scaleX: 1,
    scaleY: 1,
    angle: 0,
    clipping: false,
    mask: null,
    children,
  };

  return { plan: { group, index: at, children } };
}

/** The plan for ungrouping: the group's children take its place. */
export function planUngroup(doc: EditorDocument, id: string): GroupResult {
  const location = locateLayer(doc.layers, id);
  if (!location) return { plan: null, reason: 'not-found' };
  const group = location.siblings[location.index];
  if (!group || !isGroup(group)) return { plan: null, reason: 'not-a-group' };
  if (group.locked) return { plan: null, reason: 'locked' };
  if (group.children.length === 0) return { plan: null, reason: 'no-children' };
  return {
    plan: {
      group,
      index: location.index,
      children: group.children,
    },
  };
}

/**
 * Apply a group plan to a sibling list: the grouped layers leave the list and
 * the group arrives in their place.
 */
export function applyGroup(siblings: Layer[], plan: GroupPlan): Layer[] {
  const consumed = new Set(plan.children.map((child) => child.id));
  const kept = siblings.filter((sibling) => !consumed.has(sibling.id));
  // The group goes where the lower of the grouped layers was, which is the
  // index of the first consumed layer in the original list.
  const firstConsumed = siblings.findIndex((sibling) => consumed.has(sibling.id));
  const at = Math.max(0, Math.min(firstConsumed < 0 ? plan.index : firstConsumed, kept.length));
  kept.splice(at, 0, plan.group);
  return kept;
}

/** Apply an ungroup plan: the group leaves the list and its children arrive. */
export function applyUngroup(siblings: Layer[], plan: GroupPlan): Layer[] {
  const next = siblings.filter((sibling) => sibling.id !== plan.group.id);
  const at = Math.max(0, Math.min(plan.index, next.length));
  next.splice(at, 0, ...plan.children);
  return next;
}
